/* eslint-disable react/prop-types */
import React, { forwardRef, useState, useReducer, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, message } from 'antd';
import { useMutation } from '@apollo/client';

import useStyles from './style';
import { vettingDocumentOptions } from './index';
import { UPDATE_LANDLORD_LINKS } from '~pages/dashboard/landlord/queries';
import Modal from 'common/components/modal';
import VettingDocumentCard from './card';
import { LINK_PREFIX } from './composeLinkInput';

const getLinkItem = type => vettingDocumentOptions?.find(item => item.type === type) ?? {};

const documentReducer = (state, { type, payload }) => {
  switch (type) {
    case 'set':
      return {
        ...state,
        ...payload,
      };
    case 'add':
      return {
        ...state,
        [payload.type]: [
          ...(state[payload.type] ?? []),
          {
            link: null,
            note: null,
            linkType: getLinkItem(payload.type)?.linkType,
          },
        ],
      };
    case 'remove':
      return {
        ...state,
        [payload.type]: state[payload.type]
          .slice(0, payload.index)
          .concat(state[payload.type].slice(payload.index + 1)),
      };
    case 'change':
      return {
        ...state,
        [payload.type]: state[payload.type].map((item, idx) =>
          idx === payload.index
            ? {
                ...item,
                [payload.field]: payload.value,
              }
            : item,
        ),
      };
  }
};

const VettingDocumentModal = forwardRef((props, ref) => {
  const { form, landlord, getLandlord } = props;
  const { t } = useTranslation();

  const classes = useStyles();

  const [visible, setVisible] = useState(false);

  const [documents, dispatch] = useReducer(
    documentReducer,
    vettingDocumentOptions.reduce(
      (prev, curr) => ({
        ...prev,
        [curr.type]: [],
      }),
      {},
    ),
  );

  const [updateLandlordLinks, { loading }] = useMutation(UPDATE_LANDLORD_LINKS, {
    onCompleted: result => {
      if (result?.operateLandlordLink?.result) {
        handleCancelModal();

        getLandlord();
      }
    },
    onError: error => {
      if (error.message) {
        message.error(error.message);
      }
    },
  });

  const handleAddLink = type => {
    dispatch({
      type: 'add',
      payload: {
        type,
      },
    });
  };

  const handleRemoveLink = (type, index) => {
    dispatch({
      type: 'remove',
      payload: {
        type,
        index,
      },
    });
  };

  const handleInputChange = (type, index, field, value) => {
    dispatch({
      type: 'change',
      payload: {
        type,
        index,
        field,
        value,
      },
    });
  };

  const handleCancelModal = () => {
    setVisible(false);
  };

  const handleConfirmModal = () => {
    form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        const landlordLinks = Object.values(values).reduce(
          (prev, curr) =>
            prev.concat(
              curr?.map(item => {
                if (item.link) {
                  item.link = item.link?.includes(LINK_PREFIX)
                    ? item.link
                    : LINK_PREFIX + item.link;
                }
                return item;
              }),
            ),
          [],
        );

        updateLandlordLinks({
          variables: {
            input: {
              landlordId: landlord.id,
              landlordLinks,
            },
          },
        });
      }
    });
  };

  useImperativeHandle(ref, () => ({
    handleOpenModal: payload => {
      setVisible(true);

      dispatch({
        type: 'set',
        payload,
      });
    },
  }));

  return (
    <Modal
      visible={visible}
      title={t('cms.landlord.verification.modal.title')}
      width={880}
      wrapClassName={classes.wrapper}
      maskClosable={false}
      okText={t('cms.landlord.modal.confirm.button')}
      onCancel={handleCancelModal}
      onOk={handleConfirmModal}
      okButtonProps={{
        loading,
      }}
    >
      <Form>
        <For each="item" index="idx" of={vettingDocumentOptions}>
          <VettingDocumentCard
            form={form}
            key={item.type}
            type={item.type}
            title={t(item.name)}
            emptyText={item.emptyTextKey}
            dataSource={documents[item.type]}
            handleAddLink={handleAddLink}
            handleRemoveLink={handleRemoveLink}
            handleInputChange={handleInputChange}
          />
        </For>
      </Form>
    </Modal>
  );
});

VettingDocumentModal.displayName = 'VettingDocumentModal';

export default Form.create({
  name: 'VettingDocumentModal',
})(VettingDocumentModal);
